import { consume } from '@lit/context';
import { css, html, LitElement, nothing, PropertyValueMap } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { VersionCollapsibleStepDto, VersionDto2 } from '@/api-sdk';
import { FocusManagementMixin } from '@/components/configurator/focus-management-step-mixin';
import { global, GlobalComponentType } from '@/context';
import { BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles, collapsibleOptionsStyles } from '@/styles';
import { isScrolledIntoView, scrollStepIntoView } from '@/utils';

import '@/components/accordion/fg-accordion';
import '@/components/accordion/fg-accordion-footer';
import '@/components/accordion/fg-accordion-title';
import '@/components/configurator/step-collapsible/version/fg-collapsible-version-option';

@customElement('fg-version-stacked')
export class FgVersionStacked extends FocusManagementMixin(LitElement) {
  @property({ type: Object })
  stepData?: VersionCollapsibleStepDto;

  @property({ type: Number })
  stepIndex!: number;

  @property({ type: Boolean })
  showValidationMessage = false;

  @property({ type: Boolean })
  scrollToStep = false;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  protected willUpdate(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    if (_changedProperties.has('stepData') && this.stepData?.open) {
      this.showGoToNextStepButton = true;
    }

    if (_changedProperties.has('scrollToStep') && this.scrollToStep) {
      scrollStepIntoView(this);
      isScrolledIntoView(this);
    }

    super.willUpdate(_changedProperties);
  }

  updateComponent(queryString: string | null | undefined) {
    if (queryString === null || queryString === undefined) {
      console.error('Missing query string');
      return;
    }

    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: { option: { queryString } },
      }),
    );
  }

  isOptionFocussed(version: VersionDto2): boolean {
    if (!this.focusFirstOption) {
      return false;
    }
    const firstFocussableOptionIndex = this.stepData?.versions.findIndex(
      version => version?.disabled === false,
    );

    return (
      firstFocussableOptionIndex === this.stepData?.versions.indexOf(version)
    );
  }

  render() {
    if (this.stepData === undefined) return nothing;
    const { layout } = this.globalContext;

    return html`
      <fg-accordion .stepData=${this.stepData}>
        <fg-accordion-wrapper slot="accordion-content">
          <fg-accordion-title
            part=${layout}
            slot="accordion-wrapper-header"
            title=${this.stepData.title}
            .a11yTitle=${this.stepData.title}
            .description=${{
              selectedOption: this.stepData.selectedVersion ?? undefined,
            }}
            .editQueryString=${this.stepData.editQueryString}
            .collapseQueryString=${this.stepData.collapseQueryString}
            .open=${this.stepData.open}
            .validationMessage=${this.stepData?.validationMessage}
            .showValidationMessage=${this.showValidationMessage}
            .valid=${this.stepData.selectedVersion !== null}
            @action-edit=${() =>
              this.updateComponent(this.stepData?.editQueryString)}
          ></fg-accordion-title>
          ${this.renderNextStepButton(this.stepData.title)}
          ${this.stepData.versions.length > 0
            ? html`
                <ul
                  class="versions-wrapper"
                  slot="accordion-wrapper-body"
                  .stepIndex=${this.stepIndex}
                  aria-label=${this.stepData.title}
                >
                  ${this.stepData.versions.map(version => {
                    const focussed = this.isOptionFocussed(version);
                    return html`
                      <fg-collapsible-version-option
                        .version=${version}
                        .focussed=${focussed}
                      ></fg-collapsible-version-option>
                    `;
                  })}
                </ul>
              `
            : nothing}
        </fg-accordion-wrapper>
      </fg-accordion>
    `;
  }

  static styles = [
    baseStyles,
    collapsibleOptionsStyles,
    css`
      .versions-wrapper {
        display: grid;
        grid-template-columns: repeat(1, 1fr);
        gap: 8px;
        margin-top: 22px;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        .versions-wrapper {
          grid-template-columns: repeat(3, 1fr);
        }
      }
    `,
  ];
}
